import { useCallback, useEffect, useRef, useState } from 'react';
import { _notNil } from '@/littledash';
import type { AgentStreamEventV1, AgentStreamEventType } from '@/model/Agent.model';
import { TERMINAL_STREAM_EVENTS } from '@/model/Agent.model';

export interface UseAgentStreamOptions {
  sessionApiId: string | null;
  onEvent: (event: AgentStreamEventV1) => void;
  onError: (error: string) => void;
  onTerminal: (eventType: AgentStreamEventType) => void;
}

export interface UseAgentStreamOutput {
  isConnected: boolean;
  disconnect: () => void;
}

/**
 * Subscribes to the agent harness SSE stream for a single session. Routes each
 * event into the supplied callbacks and closes the connection on a terminal
 * stream event so the caller does not need to manage teardown.
 */
export const useAgentStream = ({
  sessionApiId,
  onEvent,
  onError,
  onTerminal,
}: UseAgentStreamOptions): UseAgentStreamOutput => {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  const onEventRef = useRef(onEvent);
  const onErrorRef = useRef(onError);
  const onTerminalRef = useRef(onTerminal);

  useEffect(() => {
    onEventRef.current = onEvent;
    onErrorRef.current = onError;
    onTerminalRef.current = onTerminal;
  });

  const disconnect = useCallback(() => {
    if (_notNil(eventSourceRef.current)) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsConnected(false);
    }
  }, []);

  useEffect(() => {
    if (sessionApiId === null) {
      return;
    }

    const url = new URL(`/api/messaging/v1/agents/sessions/${sessionApiId}/stream`, window.AppConfig.messagingApiUrl);
    const es = new EventSource(url.href, { withCredentials: true });
    eventSourceRef.current = es;

    es.onopen = () => {
      setIsConnected(true);
    };

    es.onmessage = (messageEvent: MessageEvent) => {
      let parsed: AgentStreamEventV1;
      try {
        parsed = JSON.parse(messageEvent.data) as AgentStreamEventV1;
      } catch {
        onErrorRef.current(`Failed to parse SSE event: ${messageEvent.data}`);
        return;
      }

      if (messageEvent.lastEventId !== '') {
        parsed.id = messageEvent.lastEventId;
      }

      onEventRef.current(parsed);

      if (TERMINAL_STREAM_EVENTS.has(parsed.event_type)) {
        onTerminalRef.current(parsed.event_type);
        disconnect();
      }
    };

    es.onerror = () => {
      // EventSource fires onerror for transient blips (readyState CONNECTING) as well as
      // permanent closure, and silently retries on its own. We don't want that retry loop
      // racing with hook state, so any error ends the stream: notify the caller and close.
      // The session reducer transitions to a terminal phase and refetches the durable record.
      if (eventSourceRef.current !== es) {
        return;
      }
      onErrorRef.current('SSE connection error');
      disconnect();
    };

    return () => {
      disconnect();
    };
  }, [sessionApiId, disconnect]);

  return { isConnected, disconnect };
};
